import React, {useEffect, useMemo} from 'react'
import {UserProfileSnapshotKnownPropsCodec} from '../../../common/types'
import {Select} from '../../../components/library/form'
import {useGetEnvironmentLazyQuery} from '../../../data/workbench-api/generated/environment'
import {useEnvironment} from '../../../environment'
import {useToast} from '../../../lib/toast'
import {ValidProfileSnapshotFieldTypes} from './variable-state'
import {getCustomUserFields} from '../../../selectors/customers'
import {Select as NewSelect} from '../../action-flow/components/library/select'
import {useProfileVariableNames} from './helpers'
import {VariableAction} from './actions'

// ------------ProfileVarNameForm-----------
export type ProfileVarNameFormProps = {
  profileFieldName?: string
  dispatch: React.Dispatch<VariableAction>
  exclude?: string[]
  readOnly?: boolean
  displayingInActionFlow?: boolean
}

export const ProfileVariableForm = ({
  profileFieldName,
  dispatch,
  readOnly,
  displayingInActionFlow
}: ProfileVarNameFormProps) => {
  const toaster = useToast()
  const {environment} = useEnvironment()

  const profileVariableNames = useProfileVariableNames()

  const exclude = useMemo(() => {
    return profileVariableNames.filter(p => p !== profileFieldName)
  }, [profileVariableNames, profileFieldName])

  const [query, {data, loading}] = useGetEnvironmentLazyQuery({
    onError: error => {
      toaster('Failed to fetch Custom User Fields', 'error')
      console.error(error)
    }
  })

  useEffect(() => {
    if (environment?.id) {
      query({
        variables: {id: environment?.id}
      })
    }
  }, [query, environment])

  const preferences = data?.environment?.preferences
  const fields = useMemo(
    () =>
      getCustomUserFields(preferences)
        .map(f => ({
          // 'text' or undefined -> 'string'
          type: (f.type === 'date'
            ? 'date'
            : 'string') as ValidProfileSnapshotFieldTypes,
          label: f.label,
          name: f.name
        }))
        .concat(
          Object.keys(UserProfileSnapshotKnownPropsCodec.props).map(k => ({
            type: 'string',
            label: k,
            name: k
          }))
        )
        .filter(v => !exclude.includes(v.name)),
    [preferences, exclude]
  )

  const onChange = (fieldName?: string) => {
    const newField = fields.find(f => f.name === fieldName)
    if (!newField) return
    dispatch({
      type: 'setProfileFieldName',
      payload: {
        type: newField.type,
        name: newField.name
      }
    })
  }

  const value = fields.find(f => f.name === profileFieldName)?.name
  return displayingInActionFlow ? (
    <NewSelect
      label="User profile field"
      value={value}
      onChange={onChange}
      readOnly={readOnly}
    >
      {fields.map(field => (
        <option key={field.name} value={field.name}>
          {field.name}
        </option>
      ))}
    </NewSelect>
  ) : (
    <Select
      label="User profile field"
      upLabel="User profile field"
      options={
        loading
          ? []
          : fields.map(field => ({
              text: field.name,
              value: field.name
            }))
      }
      value={value}
      onChange={onChange}
      readOnly={readOnly}
    />
  )
}
